import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Settings,
  Building2,
  Bell,
  Info,
  ChevronRight,
  ExternalLink,
  Users,
  Car,
  TrendingUp,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Save,
  X,
  Key,
  Eye,
  EyeOff,
  Check,
  XCircle,
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useNotification } from '../../contexts/NotificationContext'
import { PageHeader, InputMask, InputDecimal, SearchInput } from '../../components/ui'
import { api } from '../../services/api'
import { Panel, Card, Badge, Button } from '../../design-system'
import type {
  UsuarioConfig, UsuarioConfigCreate, UsuarioConfigUpdate, PagedResult,
  StatusVeiculo, OpcionalVeiculo,
  FunilEtapaConfig, OrigemLeadConfig, LojaConfig
} from '../../types'

const inputClass =
  'w-full h-10 rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 focus:outline-none'
const labelClass = 'block text-sm font-medium text-slate-700 mb-1'

interface ConfigSection {
  id: string
  title: string
  description: string
  icon: React.ReactNode
  link?: string
  external?: boolean
  adminOnly?: boolean
}

const configSections: ConfigSection[] = [
  {
    id: 'loja',
    title: 'Dados da Loja',
    description: 'Informações da revenda, logo, cores e configurações gerais',
    icon: <Building2 className="w-6 h-6" />,
    adminOnly: true,
  },
  {
    id: 'usuarios',
    title: 'Usuários',
    description: 'Gerenciar usuários, permissões e tipos de acesso',
    icon: <Users className="w-6 h-6" />,
    adminOnly: true,
  },
  {
    id: 'veiculos',
    title: 'Veículos',
    description: 'Configurações de status, cores e opcionais disponíveis',
    icon: <Car className="w-6 h-6" />,
    adminOnly: true,
  },
  {
    id: 'crm',
    title: 'Funil de Vendas',
    description: 'Etapas do funil, origens de leads e automações',
    icon: <TrendingUp className="w-6 h-6" />,
    adminOnly: true,
  },
  {
    id: 'notificacoes',
    title: 'Notificações',
    description: 'Configurar alertas por e-mail e notificações do sistema',
    icon: <Bell className="w-6 h-6" />,
  },
  // TODO: Implementar futuramente
  // {
  //   id: 'documentos',
  //   title: 'Modelos de Documentos',
  //   description: 'Templates de contratos, propostas e recibos',
  //   icon: <FileText className="w-6 h-6" />,
  //   adminOnly: true,
  // },
  // {
  //   id: 'seguranca',
  //   title: 'Segurança',
  //   description: 'Logs de acesso, sessões ativas e configurações de segurança',
  //   icon: <Shield className="w-6 h-6" />,
  //   adminOnly: true,
  // },
]

export default function Configuracoes() {
  const { user } = useAuth()
  const [selectedSection, setSelectedSection] = useState<string | null>(null)
  const isAdmin = user?.tipoUsuario === 'ADMIN'

  const visibleSections = configSections.filter(
    (section) => !section.adminOnly || isAdmin
  )

  const renderSectionContent = () => {
    switch (selectedSection) {
      case 'loja':
        return <LojaConfig />
      case 'usuarios':
        return <UsuariosConfig />
      case 'veiculos':
        return <VeiculosConfig />
      case 'crm':
        return <CRMConfig />
      case 'notificacoes':
        return <NotificacoesConfig />
      default:
        return (
          <div className="flex flex-col items-center justify-center py-12 text-slate-500">
            <Settings className="w-16 h-16 mb-4 text-slate-300" />
            <p className="text-lg font-medium">Em desenvolvimento</p>
            <p className="text-sm">Esta funcionalidade estará disponível em breve.</p>
          </div>
        )
    }
  }

  return (
    <div>
      <PageHeader
        title="Configurações"
        subtitle="Gerencie as configurações do sistema"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de Seções */}
        <div className="lg:col-span-1">
          <Panel title="Categorias" bodyClassName="p-0">
            <nav className="divide-y divide-slate-100">
              {visibleSections.map((section) => (
                section.link ? (
                  <Link
                    key={section.id}
                    to={section.link}
                    className="group flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex-shrink-0 grid h-10 w-10 place-items-center rounded-xl bg-slate-50 text-slate-400 ring-1 ring-slate-100 transition-colors group-hover:bg-primary-50 group-hover:text-primary-600 group-hover:ring-primary-100 [&>svg]:h-[18px] [&>svg]:w-[18px]">
                      {section.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{section.title}</p>
                      <p className="text-xs text-slate-500 truncate">{section.description}</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-400" />
                  </Link>
                ) : (
                  <button
                    key={section.id}
                    onClick={() => setSelectedSection(section.id)}
                    className={`group w-full flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors text-left ${
                      selectedSection === section.id ? 'bg-primary-50/60' : ''
                    }`}
                  >
                    <div className={`flex-shrink-0 grid h-10 w-10 place-items-center rounded-xl ring-1 transition-colors [&>svg]:h-[18px] [&>svg]:w-[18px] ${
                      selectedSection === section.id
                        ? 'bg-primary-600 text-white ring-primary-600'
                        : 'bg-slate-50 text-slate-400 ring-slate-100 group-hover:bg-primary-50 group-hover:text-primary-600 group-hover:ring-primary-100'
                    }`}>
                      {section.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{section.title}</p>
                      <p className="text-xs text-slate-500 truncate">{section.description}</p>
                    </div>
                    <ChevronRight className={`w-4 h-4 transition-transform ${
                      selectedSection === section.id ? 'text-primary-600 rotate-90' : 'text-slate-400'
                    }`} />
                  </button>
                )
              ))}
            </nav>
          </Panel>

          {/* Informações do Sistema */}
          <Card className="mt-6 p-4">
            <div className="flex items-center gap-3 text-slate-500">
              <Info className="w-5 h-5" />
              <div>
                <p className="text-sm font-medium text-slate-700">AutoGestão</p>
                <p className="text-xs">Versão 1.0.0</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Conteúdo da Seção */}
        <div className="lg:col-span-2">
          {selectedSection ? (
            <Panel
              title={visibleSections.find(s => s.id === selectedSection)?.title ?? ''}
              className="min-h-[400px]"
            >
              <p className="-mt-2 mb-4 text-sm text-slate-500">
                {visibleSections.find(s => s.id === selectedSection)?.description}
              </p>
              {renderSectionContent()}
            </Panel>
          ) : (
            <Card className="min-h-[400px]">
              <div className="flex flex-col items-center justify-center h-full py-16 text-slate-500">
                <Settings className="w-16 h-16 mb-4 text-slate-300" />
                <p className="text-lg font-medium">Selecione uma categoria</p>
                <p className="text-sm">Clique em uma opção ao lado para ver as configurações</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

// Componente de Configuração da Loja
function LojaConfig() {
  const { showSuccess, showError } = useNotification()
  const [loja, setLoja] = useState<LojaConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  // Form state
  const [razaoSocial, setRazaoSocial] = useState('')
  const [nomeFantasia, setNomeFantasia] = useState('')
  const [email, setEmail] = useState('')
  const [telefone, setTelefone] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [enderecoLogradouro, setEnderecoLogradouro] = useState('')
  const [enderecoNumero, setEnderecoNumero] = useState('')
  const [enderecoComplemento, setEnderecoComplemento] = useState('')
  const [enderecoBairro, setEnderecoBairro] = useState('')
  const [enderecoCidade, setEnderecoCidade] = useState('')
  const [enderecoUf, setEnderecoUf] = useState('')
  const [enderecoCep, setEnderecoCep] = useState('')
  const [corPrimaria, setCorPrimaria] = useState('#3B82F6')

  const loadLoja = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await api.get<LojaConfig>('/lojas/minha')
      const data = response.data
      setLoja(data)
      setRazaoSocial(data.razaoSocial || '')
      setNomeFantasia(data.nomeFantasia || '')
      setEmail(data.email || '')
      setTelefone(data.telefone || '')
      setWhatsapp(data.whatsapp || '')
      setEnderecoLogradouro(data.enderecoLogradouro || '')
      setEnderecoNumero(data.enderecoNumero || '')
      setEnderecoComplemento(data.enderecoComplemento || '')
      setEnderecoBairro(data.enderecoBairro || '')
      setEnderecoCidade(data.enderecoCidade || '')
      setEnderecoUf(data.enderecoUf || '')
      setEnderecoCep(data.enderecoCep || '')
      setCorPrimaria(data.corPrimaria || '#3B82F6')
    } catch {
      showError('Erro ao carregar dados da loja')
    } finally {
      setIsLoading(false)
    }
  }, [showError])

  useEffect(() => {
    loadLoja()
  }, [loadLoja])

  const handleCepBlur = async () => {
    const cleanCep = enderecoCep.replace(/\D/g, '')
    if (cleanCep.length !== 8) return
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`)
      const data = await response.json()
      if (data.erro) return
      setEnderecoLogradouro(data.logradouro || '')
      setEnderecoBairro(data.bairro || '')
      setEnderecoCidade(data.localidade || '')
      setEnderecoUf(data.uf || '')
    } catch { /* ignore */ }
  }

  const handleSave = async () => {
    if (!razaoSocial.trim()) {
      showError('Razão Social é obrigatória')
      return
    }

    setIsSaving(true)
    try {
      await api.put<void>('/lojas/minha', {
        razaoSocial, nomeFantasia, email, telefone, whatsapp,
        enderecoLogradouro, enderecoNumero, enderecoComplemento,
        enderecoBairro, enderecoCidade, enderecoUf, enderecoCep, corPrimaria
      })
      showSuccess('Dados da loja atualizados')
      setIsEditing(false)
      loadLoja()
    } catch {
      showError('Erro ao salvar dados')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    if (loja) {
      setRazaoSocial(loja.razaoSocial || '')
      setNomeFantasia(loja.nomeFantasia || '')
      setEmail(loja.email || '')
      setTelefone(loja.telefone || '')
      setWhatsapp(loja.whatsapp || '')
      setEnderecoLogradouro(loja.enderecoLogradouro || '')
      setEnderecoNumero(loja.enderecoNumero || '')
      setEnderecoComplemento(loja.enderecoComplemento || '')
      setEnderecoBairro(loja.enderecoBairro || '')
      setEnderecoCidade(loja.enderecoCidade || '')
      setEnderecoUf(loja.enderecoUf || '')
      setEnderecoCep(loja.enderecoCep || '')
      setCorPrimaria(loja.corPrimaria || '#3B82F6')
    }
    setIsEditing(false)
  }

  const formatCnpj = (cnpj: string) => {
    const cleaned = cnpj.replace(/\D/g, '')
    return cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
  }

  const fieldClass = (editing: boolean) =>
    `${inputClass} ${editing ? '' : 'disabled:bg-slate-50 disabled:text-slate-500'}`

  if (isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary-600" /></div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{ backgroundColor: corPrimaria }}>
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <div>
            <h3 className="font-bold tracking-tight text-slate-900">{nomeFantasia || razaoSocial}</h3>
            <p className="text-sm text-slate-500">CNPJ: {loja?.cnpj ? formatCnpj(loja.cnpj) : '-'}</p>
          </div>
        </div>
        {!isEditing && (
          <Button onClick={() => setIsEditing(true)} size="sm">
            <Pencil className="w-4 h-4" /> Editar
          </Button>
        )}
      </div>

      {/* Form */}
      <div className="space-y-4">
        <h4 className="font-semibold text-slate-900">Dados da Empresa</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Razão Social *</label>
            <input type="text" value={razaoSocial} onChange={(e) => setRazaoSocial(e.target.value)} disabled={!isEditing}
              className={fieldClass(isEditing)} />
          </div>
          <div>
            <label className={labelClass}>Nome Fantasia</label>
            <input type="text" value={nomeFantasia} onChange={(e) => setNomeFantasia(e.target.value)} disabled={!isEditing}
              className={fieldClass(isEditing)} />
          </div>
        </div>

        <h4 className="font-semibold text-slate-900 pt-2">Contato</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={!isEditing}
              className={fieldClass(isEditing)} />
          </div>
          <div>
            <label className={labelClass}>Telefone</label>
            <input type="text" value={telefone} onChange={(e) => setTelefone(e.target.value)} disabled={!isEditing}
              className={fieldClass(isEditing)} />
          </div>
          <div>
            <label className={labelClass}>WhatsApp</label>
            <input type="text" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} disabled={!isEditing}
              className={fieldClass(isEditing)} />
          </div>
        </div>

        <h4 className="font-semibold text-slate-900 pt-2">Endereço</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className={labelClass}>CEP</label>
            <input type="text" value={enderecoCep} onChange={(e) => setEnderecoCep(e.target.value)} onBlur={handleCepBlur} disabled={!isEditing} maxLength={9}
              className={fieldClass(isEditing)} />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Logradouro</label>
            <input type="text" value={enderecoLogradouro} onChange={(e) => setEnderecoLogradouro(e.target.value)} disabled={!isEditing}
              className={fieldClass(isEditing)} />
          </div>
          <div>
            <label className={labelClass}>Número</label>
            <input type="text" value={enderecoNumero} onChange={(e) => setEnderecoNumero(e.target.value)} disabled={!isEditing}
              className={fieldClass(isEditing)} />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Complemento</label>
            <input type="text" value={enderecoComplemento} onChange={(e) => setEnderecoComplemento(e.target.value)} disabled={!isEditing}
              className={fieldClass(isEditing)} />
          </div>
          <div>
            <label className={labelClass}>Bairro</label>
            <input type="text" value={enderecoBairro} onChange={(e) => setEnderecoBairro(e.target.value)} disabled={!isEditing}
              className={fieldClass(isEditing)} />
          </div>
          <div>
            <label className={labelClass}>Cidade</label>
            <input type="text" value={enderecoCidade} onChange={(e) => setEnderecoCidade(e.target.value)} disabled={!isEditing}
              className={fieldClass(isEditing)} />
          </div>
          <div>
            <label className={labelClass}>UF</label>
            <select value={enderecoUf} onChange={(e) => setEnderecoUf(e.target.value)} disabled={!isEditing}
              className={fieldClass(isEditing)}>
              <option value="">Selecione</option>
              {['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'].map((uf) => (
                <option key={uf} value={uf}>{uf}</option>
              ))}
            </select>
          </div>
        </div>

        <h4 className="font-semibold text-slate-900 pt-2">Personalização</h4>
        <div className="flex items-center gap-4">
          <div>
            <label className={labelClass}>Cor Principal</label>
            <input type="color" value={corPrimaria} onChange={(e) => setCorPrimaria(e.target.value)} disabled={!isEditing}
              className={`w-16 h-10 rounded-xl border border-slate-200 ${isEditing ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`} />
          </div>
          <div className="flex-1">
            <p className="text-sm text-slate-500">Esta cor será usada nos elementos principais do sistema.</p>
          </div>
        </div>
      </div>

      {/* Actions */}
      {isEditing && (
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
          <Button variant="secondary" onClick={handleCancel}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isSaving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      )}
    </div>
  )
}

// ==================== USUARIOS CONFIG ====================

const usuarioSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  email: z.string().email('Email inválido'),
  senha: z.string().min(6, 'Senha deve ter no mínimo 6 caracteres').optional().or(z.literal('')),
  cpf: z.string().optional(),
  tipoUsuario: z.enum(['ADMIN', 'GERENTE', 'VENDEDOR']),
  telefone: z.string().optional(),
  metaMensal: z.number().optional().nullable(),
  comissaoPercentual: z.number().min(0).max(100).optional(),
  ativo: z.boolean().optional(),
})

type UsuarioFormData = z.infer<typeof usuarioSchema>

function UsuariosConfig() {
  const { showSuccess, showError } = useNotification()
  const [usuarios, setUsuarios] = useState<UsuarioConfig[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [busca, setBusca] = useState('')
  const [filtroTipo, setFiltroTipo] = useState<string>('')
  const [filtroAtivo, setFiltroAtivo] = useState<string>('')

  // Modal states
  const [showModal, setShowModal] = useState(false)
  const [showSenhaModal, setShowSenhaModal] = useState(false)
  const [editingUser, setEditingUser] = useState<UsuarioConfig | null>(null)
  const [senhaUserId, setSenhaUserId] = useState<number | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [novaSenha, setNovaSenha] = useState('')
  const [showSenha, setShowSenha] = useState(false)

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<UsuarioFormData>({
    resolver: zodResolver(usuarioSchema),
    defaultValues: {
      tipoUsuario: 'VENDEDOR',
      comissaoPercentual: 2,
      ativo: true,
    },
  })

  const loadUsuarios = useCallback(async () => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams()
      if (busca) params.append('busca', busca)
      if (filtroTipo) params.append('tipoUsuario', filtroTipo)
      if (filtroAtivo) params.append('ativo', filtroAtivo)

      const response = await api.get<PagedResult<UsuarioConfig>>(`/usuarios?${params}`)
      setUsuarios(response.data.items)
    } catch (error) {
      showError('Erro ao carregar usuários')
    } finally {
      setIsLoading(false)
    }
  }, [busca, filtroTipo, filtroAtivo, showError])

  useEffect(() => {
    const controller = new AbortController()
    loadUsuarios()
    return () => controller.abort()
  }, [loadUsuarios])

  const openCreateModal = () => {
    setEditingUser(null)
    reset({
      nome: '',
      email: '',
      senha: '',
      cpf: '',
      tipoUsuario: 'VENDEDOR',
      telefone: '',
      metaMensal: null,
      comissaoPercentual: 2,
      ativo: true,
    })
    setShowModal(true)
  }

  const openEditModal = (usuario: UsuarioConfig) => {
    setEditingUser(usuario)
    reset({
      nome: usuario.nome,
      email: usuario.email,
      senha: '',
      cpf: usuario.cpf || '',
      tipoUsuario: usuario.tipoUsuario,
      telefone: usuario.telefone || '',
      metaMensal: usuario.metaMensal,
      comissaoPercentual: usuario.comissaoPercentual,
      ativo: usuario.ativo,
    })
    setShowModal(true)
  }

  const openSenhaModal = (userId: number) => {
    setSenhaUserId(userId)
    setNovaSenha('')
    setShowSenha(false)
    setShowSenhaModal(true)
  }

  const onSubmit = async (data: UsuarioFormData) => {
    setIsSaving(true)
    try {
      if (editingUser) {
        const updateData: UsuarioConfigUpdate = {
          nome: data.nome,
          email: data.email,
          cpf: data.cpf?.trim() || undefined,
          tipoUsuario: data.tipoUsuario,
          telefone: data.telefone,
          metaMensal: data.metaMensal ?? undefined,
          comissaoPercentual: data.comissaoPercentual ?? 2,
          ativo: data.ativo ?? true,
        }
        await api.put(`/usuarios/${editingUser.id}`, updateData)
        showSuccess('Usuário atualizado com sucesso')
      } else {
        const createData: UsuarioConfigCreate = {
          nome: data.nome,
          email: data.email,
          senha: data.senha || '',
          cpf: data.cpf?.trim() || undefined,
          tipoUsuario: data.tipoUsuario,
          telefone: data.telefone,
          metaMensal: data.metaMensal ?? undefined,
          comissaoPercentual: data.comissaoPercentual,
        }
        await api.post('/usuarios', createData)
        showSuccess('Usuário criado com sucesso')
      }
      setShowModal(false)
      loadUsuarios()
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      showError(err.response?.data?.message || 'Erro ao salvar usuário')
    } finally {
      setIsSaving(false)
    }
  }

  const handleAlterarSenha = async () => {
    if (!senhaUserId || !novaSenha) return
    if (novaSenha.length < 6) {
      showError('Senha deve ter no mínimo 6 caracteres')
      return
    }

    setIsSaving(true)
    try {
      await api.put(`/usuarios/${senhaUserId}/senha`, { novaSenha })
      showSuccess('Senha alterada com sucesso')
      setShowSenhaModal(false)
    } catch (error) {
      showError('Erro ao alterar senha')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDesativar = async (id: number) => {
    if (!confirm('Deseja realmente desativar este usuário?')) return

    try {
      await api.delete(`/usuarios/${id}`)
      showSuccess('Usuário desativado')
      loadUsuarios()
    } catch (error) {
      showError('Erro ao desativar usuário')
    }
  }

  const getTipoLabel = (tipo: string) => {
    switch (tipo) {
      case 'ADMIN': return 'Administrador'
      case 'GERENTE': return 'Gerente'
      case 'VENDEDOR': return 'Vendedor'
      default: return tipo
    }
  }

  const getTipoBadgeTone = (tipo: string): 'primary' | 'info' | 'success' | 'neutral' => {
    switch (tipo) {
      case 'ADMIN': return 'primary'
      case 'GERENTE': return 'info'
      case 'VENDEDOR': return 'success'
      default: return 'neutral'
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap gap-4 items-center">
        <SearchInput
          value={busca}
          onChange={setBusca}
          placeholder="Buscar por nome, email ou CPF..."
        />
        <select
          value={filtroTipo}
          onChange={(e) => setFiltroTipo(e.target.value)}
          className={`${inputClass} w-auto`}
        >
          <option value="">Todos os tipos</option>
          <option value="ADMIN">Administrador</option>
          <option value="GERENTE">Gerente</option>
          <option value="VENDEDOR">Vendedor</option>
        </select>
        <select
          value={filtroAtivo}
          onChange={(e) => setFiltroAtivo(e.target.value)}
          className={`${inputClass} w-auto`}
        >
          <option value="">Todos</option>
          <option value="true">Ativos</option>
          <option value="false">Inativos</option>
        </select>
        <Button onClick={openCreateModal} className="ml-auto">
          <Plus className="w-4 h-4" />
          Novo Usuário
        </Button>
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : usuarios.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p>Nenhum usuário encontrado</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left p-3 font-medium text-slate-700">Nome</th>
                <th className="text-left p-3 font-medium text-slate-700">Email</th>
                <th className="text-left p-3 font-medium text-slate-700">Tipo</th>
                <th className="text-center p-3 font-medium text-slate-700">Status</th>
                <th className="text-right p-3 font-medium text-slate-700">Ações</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map((usuario) => (
                <tr key={usuario.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-3">
                    <div className="font-medium text-slate-900">{usuario.nome}</div>
                    {usuario.telefone && (
                      <div className="text-xs text-slate-500">{usuario.telefone}</div>
                    )}
                  </td>
                  <td className="p-3 text-slate-600">{usuario.email}</td>
                  <td className="p-3">
                    <Badge tone={getTipoBadgeTone(usuario.tipoUsuario)}>
                      {getTipoLabel(usuario.tipoUsuario)}
                    </Badge>
                  </td>
                  <td className="p-3 text-center">
                    {usuario.ativo ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600">
                        <Check className="w-4 h-4" />
                        Ativo
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-rose-600">
                        <XCircle className="w-4 h-4" />
                        Inativo
                      </span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => openEditModal(usuario)}
                        className="p-1.5 text-slate-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openSenhaModal(usuario.id)}
                        className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                        title="Alterar Senha"
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      {usuario.ativo && (
                        <button
                          onClick={() => handleDesativar(usuario.id)}
                          className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                          title="Desativar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Usuário */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="text-lg font-bold tracking-tight text-slate-900">
                {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={labelClass}>Nome *</label>
                  <input
                    type="text"
                    {...register('nome')}
                    className={inputClass}
                  />
                  {errors.nome && <p className="text-sm text-rose-600 mt-1">{errors.nome.message}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>Email *</label>
                  <input
                    type="email"
                    {...register('email')}
                    className={inputClass}
                  />
                  {errors.email && <p className="text-sm text-rose-600 mt-1">{errors.email.message}</p>}
                </div>

                {!editingUser && (
                  <div className="md:col-span-2">
                    <label className={labelClass}>Senha *</label>
                    <input
                      type="password"
                      {...register('senha')}
                      placeholder="Mínimo 6 caracteres"
                      className={inputClass}
                    />
                    {errors.senha && <p className="text-sm text-rose-600 mt-1">{errors.senha.message}</p>}
                  </div>
                )}

                <Controller
                  name="cpf"
                  control={control}
                  render={({ field }) => (
                    <InputMask
                      label="CPF"
                      mask="cpf"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="000.000.000-00"
                    />
                  )}
                />

                <Controller
                  name="telefone"
                  control={control}
                  render={({ field }) => (
                    <InputMask
                      label="Telefone"
                      mask="cellphone"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="(00) 00000-0000"
                    />
                  )}
                />

                <div>
                  <label className={labelClass}>Tipo de Usuário *</label>
                  <select
                    {...register('tipoUsuario')}
                    className={inputClass}
                  >
                    <option value="VENDEDOR">Vendedor</option>
                    <option value="GERENTE">Gerente</option>
                    <option value="ADMIN">Administrador</option>
                  </select>
                </div>

                <Controller
                  name="comissaoPercentual"
                  control={control}
                  render={({ field }) => (
                    <div>
                      <label className={labelClass}>Comissão (%)</label>
                      <InputDecimal
                        value={field.value ?? 0}
                        onChange={field.onChange}
                        decimals={2}
                        suffix="%"
                        className={inputClass}
                      />
                    </div>
                  )}
                />

                <Controller
                  name="metaMensal"
                  control={control}
                  render={({ field }) => (
                    <div>
                      <label className={labelClass}>Meta Mensal (R$)</label>
                      <InputDecimal
                        value={field.value ?? 0}
                        onChange={field.onChange}
                        decimals={2}
                        prefix="R$ "
                        className={inputClass}
                      />
                    </div>
                  )}
                />

                {editingUser && (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="ativo"
                      {...register('ativo')}
                      className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor="ativo" className="text-sm font-medium text-slate-700">
                      Usuário Ativo
                    </label>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={isSaving}>
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {isSaving ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Alterar Senha */}
      {showSenhaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="text-lg font-bold tracking-tight text-slate-900">Alterar Senha</h3>
              <button onClick={() => setShowSenhaModal(false)} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className={labelClass}>Nova Senha *</label>
                <div className="relative">
                  <input
                    type={showSenha ? 'text' : 'password'}
                    value={novaSenha}
                    onChange={(e) => setNovaSenha(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    className={`${inputClass} pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowSenha(!showSenha)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="secondary" onClick={() => setShowSenhaModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleAlterarSenha} disabled={isSaving || !novaSenha}>
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                  {isSaving ? 'Alterando...' : 'Alterar Senha'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ==================== VEICULOS CONFIG ====================

type VeiculosTab = 'status' | 'opcionais'

function VeiculosConfig() {
  const [activeTab, setActiveTab] = useState<VeiculosTab>('status')

  const tabs = [
    { id: 'status' as VeiculosTab, label: 'Status' },
    { id: 'opcionais' as VeiculosTab, label: 'Opcionais' },
  ]

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'status' && <StatusConfig />}
      {activeTab === 'opcionais' && <OpcionaisConfig />}
    </div>
  )
}

// ==================== STATUS CONFIG ====================

function StatusConfig() {
  const { showSuccess, showError } = useNotification()
  const [items, setItems] = useState<StatusVeiculo[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingItem, setEditingItem] = useState<StatusVeiculo | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  // Form state
  const [codigo, setCodigo] = useState('')
  const [nome, setNome] = useState('')
  const [corBadge, setCorBadge] = useState('gray')
  const [permiteVenda, setPermiteVenda] = useState(true)
  const [ordem, setOrdem] = useState(0)

  const badgeColors = ['gray', 'green', 'yellow', 'red', 'blue', 'purple', 'orange']

  const loadItems = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await api.get<StatusVeiculo[]>('/status-veiculo?incluirInativos=true')
      setItems(response.data)
    } catch {
      showError('Erro ao carregar status')
    } finally {
      setIsLoading(false)
    }
  }, [showError])

  useEffect(() => {
    loadItems()
  }, [loadItems])

  const openCreate = () => {
    setEditingItem(null)
    setCodigo('')
    setNome('')
    setCorBadge('gray')
    setPermiteVenda(true)
    setOrdem(items.length + 1)
    setShowModal(true)
  }

  const openEdit = (item: StatusVeiculo) => {
    setEditingItem(item)
    setCodigo(item.codigo)
    setNome(item.nome)
    setCorBadge(item.corBadge)
    setPermiteVenda(item.permiteVenda)
    setOrdem(item.ordem)
    setShowModal(true)
  }

  const handleSave = async () => {
    if (!codigo.trim() || !nome.trim()) {
      showError('Código e Nome são obrigatórios')
      return
    }

    setIsSaving(true)
    try {
      if (editingItem) {
        await api.put(`/status-veiculo/${editingItem.id}`, { codigo, nome, corBadge, permiteVenda, ordem, ativo: editingItem.ativo })
        showSuccess('Status atualizado')
      } else {
        await api.post('/status-veiculo', { codigo, nome, corBadge, permiteVenda, ordem })
        showSuccess('Status criado')
      }
      setShowModal(false)
      loadItems()
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      showError(err.response?.data?.message || 'Erro ao salvar')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Desativar este status?')) return
    try {
      await api.delete(`/status-veiculo/${id}`)
      showSuccess('Status desativado')
      loadItems()
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      showError(err.response?.data?.message || 'Erro ao desativar')
    }
  }

  const getBadgeStyle = (color: string) => {
    const styles: Record<string, string> = {
      gray: 'bg-gray-100 text-gray-800',
      green: 'bg-green-100 text-green-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      red: 'bg-red-100 text-red-800',
      blue: 'bg-blue-100 text-blue-800',
      purple: 'bg-purple-100 text-purple-800',
      orange: 'bg-orange-100 text-orange-800',
    }
    return styles[color] || styles.gray
  }

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary-600" /></div>
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openCreate} size="sm">
          <Plus className="w-4 h-4" /> Novo Status
        </Button>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-100 bg-slate-50">
            <th className="text-left p-2 font-medium text-slate-700">Código</th>
            <th className="text-left p-2 font-medium text-slate-700">Nome</th>
            <th className="text-center p-2 font-medium text-slate-700">Permite Venda</th>
            <th className="text-right p-2 font-medium text-slate-700">Ações</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} className={`border-b border-slate-100 ${!item.ativo ? 'opacity-50' : ''}`}>
              <td className="p-2 font-mono text-xs text-slate-600">{item.codigo}</td>
              <td className="p-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getBadgeStyle(item.corBadge)}`}>
                  {item.nome}
                </span>
              </td>
              <td className="p-2 text-center">
                {item.permiteVenda ? <Check className="w-4 h-4 text-emerald-600 mx-auto" /> : <X className="w-4 h-4 text-slate-400 mx-auto" />}
              </td>
              <td className="p-2">
                <div className="flex justify-end gap-1">
                  <button onClick={() => openEdit(item)} className="p-1 text-slate-500 hover:text-primary-600 transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                  {item.ativo && (
                    <button onClick={() => handleDelete(item.id)} className="p-1 text-slate-500 hover:text-rose-600 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="font-bold tracking-tight text-slate-900">{editingItem ? 'Editar Status' : 'Novo Status'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Código *</label>
                  <input
                    type="text"
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value.toUpperCase().replace(/[^A-Z_]/g, ''))}
                    placeholder="EX: RESERVADO"
                    className={`${inputClass} font-mono`}
                  />
                </div>
                <div>
                  <label className={labelClass}>Nome *</label>
                  <input
                    type="text"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>Cor do Badge</label>
                <div className="flex gap-2">
                  {badgeColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setCorBadge(color)}
                      className={`px-3 py-1 rounded-lg text-xs font-medium ${getBadgeStyle(color)} ${corBadge === color ? 'ring-2 ring-primary-500' : ''}`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={permiteVenda}
                    onChange={(e) => setPermiteVenda(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-slate-700">Permite Venda</span>
                </label>
                <div>
                  <label className="text-sm text-slate-700 mr-2">Ordem:</label>
                  <input
                    type="number"
                    value={ordem}
                    onChange={(e) => setOrdem(Number(e.target.value))}
                    className="w-16 h-10 rounded-xl border border-slate-200 bg-white text-center text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 focus:outline-none"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Salvar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ==================== OPCIONAIS CONFIG ====================

function OpcionaisConfig() {
  const { showSuccess, showError } = useNotification()
  const [items, setItems] = useState<OpcionalVeiculo[]>([])
  const [categorias, setCategorias] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingItem, setEditingItem] = useState<OpcionalVeiculo | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [filtroCategoria, setFiltroCategoria] = useState('')

  // Form state
  const [nome, setNome] = useState('')
  const [categoria, setCategoria] = useState('')
  const [ordem, setOrdem] = useState(0)

  const loadItems = useCallback(async () => {
    setIsLoading(true)
    try {
      const [opcionaisRes, categoriasRes] = await Promise.all([
        api.get<OpcionalVeiculo[]>('/opcionais-veiculo?incluirInativos=true'),
        api.get<string[]>('/opcionais-veiculo/categorias'),
      ])
      setItems(opcionaisRes.data)
      setCategorias(categoriasRes.data)
    } catch {
      showError('Erro ao carregar opcionais')
    } finally {
      setIsLoading(false)
    }
  }, [showError])

  useEffect(() => {
    loadItems()
  }, [loadItems])

  const openCreate = () => {
    setEditingItem(null)
    setNome('')
    setCategoria(filtroCategoria || 'CONFORTO')
    setOrdem(items.length + 1)
    setShowModal(true)
  }

  const openEdit = (item: OpcionalVeiculo) => {
    setEditingItem(item)
    setNome(item.nome)
    setCategoria(item.categoria || '')
    setOrdem(item.ordem)
    setShowModal(true)
  }

  const handleSave = async () => {
    if (!nome.trim()) {
      showError('Nome é obrigatório')
      return
    }

    setIsSaving(true)
    try {
      if (editingItem) {
        await api.put(`/opcionais-veiculo/${editingItem.id}`, { nome, categoria, ordem, ativo: editingItem.ativo })
        showSuccess('Opcional atualizado')
      } else {
        await api.post('/opcionais-veiculo', { nome, categoria, ordem })
        showSuccess('Opcional criado')
      }
      setShowModal(false)
      loadItems()
    } catch {
      showError('Erro ao salvar')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Desativar este opcional?')) return
    try {
      await api.delete(`/opcionais-veiculo/${id}`)
      showSuccess('Opcional desativado')
      loadItems()
    } catch {
      showError('Erro ao desativar')
    }
  }

  const filteredItems = filtroCategoria
    ? items.filter((i) => i.categoria === filtroCategoria)
    : items

  const groupedItems = filteredItems.reduce((acc, item) => {
    const cat = item.categoria || 'OUTROS'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(item)
    return acc
  }, {} as Record<string, OpcionalVeiculo[]>)

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary-600" /></div>
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <select
          value={filtroCategoria}
          onChange={(e) => setFiltroCategoria(e.target.value)}
          className={`${inputClass} w-auto`}
        >
          <option value="">Todas as categorias</option>
          {categorias.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <Button onClick={openCreate} size="sm">
          <Plus className="w-4 h-4" /> Novo Opcional
        </Button>
      </div>

      {Object.entries(groupedItems).map(([cat, catItems]) => (
        <div key={cat} className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 font-medium text-sm text-slate-700">{cat}</div>
          <div className="divide-y divide-slate-100">
            {catItems.map((item) => (
              <div key={item.id} className={`flex items-center justify-between px-4 py-2 ${!item.ativo ? 'opacity-50' : ''}`}>
                <span className="text-sm text-slate-700">{item.nome}</span>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(item)} className="p-1 text-slate-500 hover:text-primary-600 transition-colors">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  {item.ativo && (
                    <button onClick={() => handleDelete(item.id)} className="p-1 text-slate-500 hover:text-rose-600 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="font-bold tracking-tight text-slate-900">{editingItem ? 'Editar Opcional' : 'Novo Opcional'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className={labelClass}>Nome *</label>
                <input
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Categoria</label>
                  <select
                    value={categoria}
                    onChange={(e) => setCategoria(e.target.value)}
                    className={inputClass}
                  >
                    <option value="CONFORTO">Conforto</option>
                    <option value="SEGURANCA">Segurança</option>
                    <option value="TECNOLOGIA">Tecnologia</option>
                    <option value="EXTERIOR">Exterior</option>
                    <option value="INTERIOR">Interior</option>
                    <option value="OUTROS">Outros</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Ordem</label>
                  <input
                    type="number"
                    value={ordem}
                    onChange={(e) => setOrdem(Number(e.target.value))}
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Salvar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ==================== CRM CONFIG ====================

type CRMTab = 'etapas' | 'origens'

function CRMConfig() {
  const [activeTab, setActiveTab] = useState<CRMTab>('etapas')

  const tabs = [
    { id: 'etapas' as CRMTab, label: 'Etapas do Funil' },
    { id: 'origens' as CRMTab, label: 'Origens de Leads' },
  ]

  return (
    <div className="space-y-4">
      <div className="flex gap-2 border-b border-slate-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'etapas' && <EtapasConfig />}
      {activeTab === 'origens' && <OrigensConfig />}
    </div>
  )
}

function EtapasConfig() {
  const { showSuccess, showError } = useNotification()
  const [items, setItems] = useState<FunilEtapaConfig[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingItem, setEditingItem] = useState<FunilEtapaConfig | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const [nome, setNome] = useState('')
  const [cor, setCor] = useState('#3B82F6')
  const [prazoHoras, setPrazoHoras] = useState<number | undefined>()
  const [ordem, setOrdem] = useState(1)
  const [ativo, setAtivo] = useState(true)

  const loadItems = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await api.get<FunilEtapaConfig[]>('/funil/etapas?incluirInativos=true')
      setItems(response.data)
    } catch {
      showError('Erro ao carregar etapas')
    } finally {
      setIsLoading(false)
    }
  }, [showError])

  useEffect(() => {
    loadItems()
  }, [loadItems])

  const openCreate = () => {
    setEditingItem(null)
    setNome('')
    setCor('#3B82F6')
    setPrazoHoras(undefined)
    setOrdem(items.length + 1)
    setShowModal(true)
  }

  const openEdit = (item: FunilEtapaConfig) => {
    setEditingItem(item)
    setNome(item.nome)
    setCor(item.cor)
    setPrazoHoras(item.prazoHoras)
    setOrdem(item.ordem)
    setAtivo(item.ativo)
    setShowModal(true)
  }

  const handleSave = async () => {
    if (!nome.trim()) {
      showError('Nome é obrigatório')
      return
    }

    setIsSaving(true)
    try {
      if (editingItem) {
        await api.put(`/funil/etapas/${editingItem.id}`, { nome, cor, ordem, prazoHoras, ativo })
        showSuccess('Etapa atualizada')
      } else {
        await api.post('/funil/etapas', { nome, cor, prazoHoras })
        showSuccess('Etapa criada')
      }
      setShowModal(false)
      loadItems()
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      showError(err.response?.data?.message || 'Erro ao salvar')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Desativar esta etapa?')) return
    try {
      await api.delete(`/funil/etapas/${id}`)
      showSuccess('Etapa desativada')
      loadItems()
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      showError(err.response?.data?.message || 'Erro ao desativar')
    }
  }

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary-600" /></div>
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openCreate} size="sm">
          <Plus className="w-4 h-4" /> Nova Etapa
        </Button>
      </div>

      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.id} className={`flex items-center gap-4 p-3 border border-slate-200 rounded-xl ${!item.ativo ? 'opacity-50' : ''}`}>
            <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: item.cor }} />
            <div className="flex-1">
              <p className="font-medium text-sm text-slate-900">{item.nome}</p>
              <p className="text-xs text-slate-500">
                {item.quantidadeLeads} lead(s) • Ordem: {item.ordem}
                {item.prazoHoras && ` • Prazo: ${item.prazoHoras}h`}
              </p>
            </div>
            <div className="flex gap-1">
              <button onClick={() => openEdit(item)} className="p-1.5 text-slate-500 hover:text-primary-600 transition-colors">
                <Pencil className="w-4 h-4" />
              </button>
              {item.ativo && (
                <button onClick={() => handleDelete(item.id)} className="p-1.5 text-slate-500 hover:text-rose-600 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="font-bold tracking-tight text-slate-900">{editingItem ? 'Editar Etapa' : 'Nova Etapa'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className={labelClass}>Nome *</label>
                <input
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Cor</label>
                  <input
                    type="color"
                    value={cor}
                    onChange={(e) => setCor(e.target.value)}
                    className="w-full h-10 rounded-xl border border-slate-200 cursor-pointer"
                  />
                </div>
                <div>
                  <label className={labelClass}>Prazo (horas)</label>
                  <input
                    type="number"
                    value={prazoHoras || ''}
                    onChange={(e) => setPrazoHoras(e.target.value ? Number(e.target.value) : undefined)}
                    placeholder="Opcional"
                    className={inputClass}
                  />
                </div>
              </div>
              {editingItem && (
                <>
                  <div>
                    <label className={labelClass}>Ordem</label>
                    <input
                      type="number"
                      value={ordem}
                      onChange={(e) => setOrdem(Number(e.target.value))}
                      className={inputClass}
                    />
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={ativo}
                      onChange={(e) => setAtivo(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-slate-700">Ativo</span>
                  </label>
                </>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Salvar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function OrigensConfig() {
  const { showSuccess, showError } = useNotification()
  const [items, setItems] = useState<OrigemLeadConfig[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingItem, setEditingItem] = useState<OrigemLeadConfig | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const [nome, setNome] = useState('')
  const [cor, setCor] = useState('#6B7280')
  const [ativoOrigem, setAtivoOrigem] = useState(true)

  const loadItems = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await api.get<OrigemLeadConfig[]>('/funil/origens?incluirInativos=true')
      setItems(response.data)
    } catch {
      showError('Erro ao carregar origens')
    } finally {
      setIsLoading(false)
    }
  }, [showError])

  useEffect(() => {
    loadItems()
  }, [loadItems])

  const openCreate = () => {
    setEditingItem(null)
    setNome('')
    setCor('#6B7280')
    setShowModal(true)
  }

  const openEdit = (item: OrigemLeadConfig) => {
    setEditingItem(item)
    setNome(item.nome)
    setCor(item.cor)
    setAtivoOrigem(item.ativo)
    setShowModal(true)
  }

  const handleSave = async () => {
    if (!nome.trim()) {
      showError('Nome é obrigatório')
      return
    }

    setIsSaving(true)
    try {
      if (editingItem) {
        await api.put(`/funil/origens/${editingItem.id}`, { nome, cor, ativo: ativoOrigem })
        showSuccess('Origem atualizada')
      } else {
        await api.post('/funil/origens', { nome, cor })
        showSuccess('Origem criada')
      }
      setShowModal(false)
      loadItems()
    } catch {
      showError('Erro ao salvar')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Desativar esta origem?')) return
    try {
      await api.delete(`/funil/origens/${id}`)
      showSuccess('Origem desativada')
      loadItems()
    } catch {
      showError('Erro ao desativar')
    }
  }

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary-600" /></div>
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openCreate} size="sm">
          <Plus className="w-4 h-4" /> Nova Origem
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {items.map((item) => (
          <div key={item.id} className={`p-3 border border-slate-200 rounded-xl ${!item.ativo ? 'opacity-50' : ''}`}>
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.cor }} />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-slate-900 truncate">{item.nome}</p>
                <p className="text-xs text-slate-500">{item.quantidadeLeads} lead(s)</p>
              </div>
            </div>
            <div className="flex justify-end gap-1 mt-2">
              <button onClick={() => openEdit(item)} className="p-1 text-slate-500 hover:text-primary-600 transition-colors">
                <Pencil className="w-3.5 h-3.5" />
              </button>
              {item.ativo && (
                <button onClick={() => handleDelete(item.id)} className="p-1 text-slate-500 hover:text-rose-600 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="font-bold tracking-tight text-slate-900">{editingItem ? 'Editar Origem' : 'Nova Origem'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className={labelClass}>Nome *</label>
                <input
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Ex: Site, WhatsApp, Indicação..."
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Cor</label>
                <input
                  type="color"
                  value={cor}
                  onChange={(e) => setCor(e.target.value)}
                  className="w-full h-10 rounded-xl border border-slate-200 cursor-pointer"
                />
              </div>
              {editingItem && (
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ativoOrigem}
                    onChange={(e) => setAtivoOrigem(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-slate-700">Ativo</span>
                </label>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Salvar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Componente de Configuração de Notificações
function NotificacoesConfig() {
  const [emailVenda, setEmailVenda] = useState(true)
  const [emailLead, setEmailLead] = useState(true)
  const [emailEstoque, setEmailEstoque] = useState(false)

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold text-slate-900 mb-4">Notificações por E-mail</h3>
        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={emailVenda}
              onChange={(e) => setEmailVenda(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
            />
            <div>
              <p className="text-sm font-medium text-slate-900">Nova venda realizada</p>
              <p className="text-xs text-slate-500">Receber e-mail quando uma venda for concluída</p>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={emailLead}
              onChange={(e) => setEmailLead(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
            />
            <div>
              <p className="text-sm font-medium text-slate-900">Novo lead cadastrado</p>
              <p className="text-xs text-slate-500">Receber e-mail quando um novo lead entrar no funil</p>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={emailEstoque}
              onChange={(e) => setEmailEstoque(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
            />
            <div>
              <p className="text-sm font-medium text-slate-900">Alerta de estoque</p>
              <p className="text-xs text-slate-500">Receber e-mail sobre veículos há muito tempo no estoque</p>
            </div>
          </label>
        </div>
      </div>

      <div className="p-4 bg-sky-50 border border-sky-200 rounded-xl">
        <p className="text-sm text-sky-800">
          <strong>Nota:</strong> As preferências de notificação serão salvas automaticamente quando o backend for implementado.
        </p>
      </div>
    </div>
  )
}
